/**
 * feat-299 — which items on `/quiz-review` are waiting for approval (spec §5).
 *
 * The source of truth is feat-270's EXISTING read endpoints, never a guess:
 * "pending" is derived server-side by comparing the live row with the approved
 * snapshot (ADR-feat-270 §3), so a save that restores the approved text is NOT
 * pending. This module therefore never marks anything pending because "I just
 * saved it" — after a save it re-reads that Lesson (`getApprovalLessonDetail`)
 * and believes the answer (AT-12).
 *
 * - Initial load: `getApprovalQueue(item_limit=200, lessons_limit=200)`.
 *   `approval_required: false` ⇒ nothing else is ever fetched (AT-11).
 * - A Lesson whose items were windowed (`items_truncated`) is re-read in full
 *   through the Lesson detail, paging until it is not truncated, so a chip is
 *   never missing because of the window ("never show a false approved").
 * - The queue has no truncation flag for the Lesson list itself; when it comes
 *   back AT the 200 cap, the summary names every pending Lesson and the missing
 *   ones are read one by one.
 * - Any failure: no chips, `console.error`, and the page and editing carry on
 *   (AT-13) — the same degrade-don't-break shape as the translation-languages
 *   side fetch.
 *
 * READ ONLY. Nothing here imports, or may import, an approve/revert function
 * (AT-14).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApprovalChangeKind,
  ApprovalLessonDetail,
  getApprovalLessonDetail,
  getApprovalQueue,
  getApprovalSummary,
  LessonApprovalStatus,
} from '../../../services/curriculumApprovalService';

export const PENDING_ITEM_LIMIT = 200;
export const PENDING_LESSONS_LIMIT = 200;
/** Page size for the per-Lesson detail read (the endpoint's `limit` ≤ 500). */
export const LESSON_DETAIL_PAGE = 500;

export interface LessonPending {
  state: LessonApprovalStatus;
  /** Server's `pending_item_count` — read, never counted here. */
  count: number;
  /** `entity_type:entity_id` → change kind. */
  items: Map<string, ApprovalChangeKind>;
}

export type PendingState =
  | { status: 'none' } // no tenant
  | { status: 'not-required' } // the tenant's approval gate is off
  | { status: 'loading' }
  | { status: 'failed' }
  | {
      status: 'ready';
      lessons: ReadonlyMap<string, LessonPending>;
      lessonsPending: number;
      changesPending: number;
    };

export const pendingKey = (entityType: string, entityId: string): string =>
  `${entityType}:${entityId}`;

function fromDetail(detail: ApprovalLessonDetail, into?: LessonPending): LessonPending {
  const out: LessonPending = into ?? {
    state: detail.state,
    count: detail.pending_item_count,
    items: new Map(),
  };
  for (const item of detail.items) {
    // A deleted item has no row on this page to put a chip on.
    if (item.change_kind === 'deleted') continue;
    out.items.set(
      pendingKey(item.entity_ref.entity_type, item.entity_ref.entity_id),
      item.change_kind,
    );
  }
  return out;
}

/** One Lesson's complete pending set, paging past the detail window. */
async function fetchLessonPending(tenantId: string, lessonId: string): Promise<LessonPending> {
  let page = await getApprovalLessonDetail(tenantId, lessonId, { limit: LESSON_DETAIL_PAGE });
  const out = fromDetail(page);
  let offset = page.items_returned;
  while (page.items_truncated && page.items_returned > 0) {
    // eslint-disable-next-line no-await-in-loop
    page = await getApprovalLessonDetail(tenantId, lessonId, {
      limit: LESSON_DETAIL_PAGE,
      offset,
    });
    fromDetail(page, out);
    offset += page.items_returned;
  }
  return out;
}

export async function loadPending(tenantId: string): Promise<PendingState> {
  const queue = await getApprovalQueue(tenantId, {
    itemLimit: PENDING_ITEM_LIMIT,
    lessonsLimit: PENDING_LESSONS_LIMIT,
  });
  if (!queue.approval_required) return { status: 'not-required' };

  const lessons = new Map<string, LessonPending>();
  const reread: string[] = [];
  for (const l of queue.lessons) {
    lessons.set(l.lesson_id, fromDetail(l));
    if (l.items_truncated) reread.push(l.lesson_id);
  }
  if (queue.lessons.length >= PENDING_LESSONS_LIMIT) {
    const summary = await getApprovalSummary(tenantId);
    for (const s of summary.lessons) {
      if (s.pending_item_count > 0 && !lessons.has(s.lesson_id)) reread.push(s.lesson_id);
    }
  }
  const full = await Promise.all(reread.map((id) => fetchLessonPending(tenantId, id)));
  reread.forEach((id, i) => lessons.set(id, full[i]));
  return {
    status: 'ready',
    lessons,
    lessonsPending: queue.lessons_pending,
    changesPending: queue.changes_pending,
  };
}

export interface PendingApi {
  state: PendingState;
  /** Re-read one Lesson after a successful save (AT-12). While the initial
   *  load is in flight the re-read is queued and runs once it is ready (QA-F4);
   *  otherwise a no-op unless ready. */
  refreshLesson: (lessonId: string) => void;
}

export function usePendingApprovals(tenantId: string | null | undefined): PendingApi {
  const [state, setState] = useState<PendingState>(
    tenantId ? { status: 'loading' } : { status: 'none' },
  );
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;
  /** Per-Lesson request sequence, so an older answer never overwrites a newer one. */
  const seqRef = useRef(new Map<string, number>());
  /**
   * QA-F4: Lessons saved while the initial queue read was in flight. That
   * response may have been computed before the write, so each is re-read as
   * soon as the load lands `ready` — never trusted as-is.
   */
  const deferredRef = useRef(new Set<string>());
  /** The load's status, set synchronously alongside each `setState` (unlike
   *  `state`, which is only read after a render), so a save landing between the load
   *  settling and the next render is neither lost nor sent when not required. */
  const statusRef = useRef<PendingState['status']>(state.status);

  const runRefresh = useCallback((tenant: string, lessonId: string) => {
    const seq = (seqRef.current.get(lessonId) ?? 0) + 1;
    seqRef.current.set(lessonId, seq);
    (async () => {
      try {
        const fresh = await fetchLessonPending(tenant, lessonId);
        if (tenantRef.current !== tenant || seqRef.current.get(lessonId) !== seq) return;
        setState((prev) => {
          if (prev.status !== 'ready') return prev;
          const lessons = new Map(prev.lessons);
          const before = lessons.get(lessonId);
          const beforeCount = before?.count ?? 0;
          if (fresh.state === 'approved' && fresh.count === 0) lessons.delete(lessonId);
          else lessons.set(lessonId, fresh);
          // The page-note totals move by THIS Lesson's server-reported delta;
          // nothing is recounted from the map (which may be windowed).
          return {
            status: 'ready',
            lessons,
            lessonsPending:
              prev.lessonsPending + (fresh.count > 0 ? 1 : 0) - (beforeCount > 0 ? 1 : 0),
            changesPending: prev.changesPending + fresh.count - beforeCount,
          };
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[QuizContentReview] GET approvals/lessons failed:', e);
      }
    })();
  }, []);

  // Keyed on TENANT ONLY: EN/ES/Both must fire no request (feat-284 AC4).
  useEffect(() => {
    let cancelled = false;
    seqRef.current = new Map();
    deferredRef.current = new Set();
    if (!tenantId) {
      statusRef.current = 'none';
      setState({ status: 'none' });
      return undefined;
    }
    statusRef.current = 'loading';
    setState({ status: 'loading' });
    (async () => {
      try {
        const next = await loadPending(tenantId);
        if (cancelled) return;
        statusRef.current = next.status;
        setState(next);
        // Re-read every Lesson saved during the load. The `ready` state is
        // queued first, so each re-read's update applies on top of it.
        const deferred = Array.from(deferredRef.current);
        deferredRef.current = new Set();
        if (next.status === 'ready') deferred.forEach((id) => runRefresh(tenantId, id));
      } catch (e) {
        if (cancelled) return;
        statusRef.current = 'failed';
        deferredRef.current = new Set();
        // eslint-disable-next-line no-console
        console.error('[QuizContentReview] GET approvals/pending failed:', e);
        setState({ status: 'failed' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, runRefresh]);

  const refreshLesson = useCallback(
    (lessonId: string) => {
      const tenant = tenantRef.current;
      if (!tenant) return;
      if (statusRef.current === 'loading') {
        deferredRef.current.add(lessonId);
        return;
      }
      if (statusRef.current !== 'ready') return;
      runRefresh(tenant, lessonId);
    },
    [runRefresh],
  );

  return { state, refreshLesson };
}

// ------------------------------------------------------------ lookups

export type ChipKind = 'pending-updated' | 'pending-created' | 'not-live';

/** The chip for an item/Drill inside `lessonId`, or null. A never-approved
 *  Lesson suppresses every chip inside it (design Q4). */
export function itemChip(
  state: PendingState,
  lessonId: string | null,
  entityType: string,
  entityId: string,
): ChipKind | null {
  if (state.status !== 'ready' || !lessonId) return null;
  const lesson = state.lessons.get(lessonId);
  if (!lesson || lesson.state === 'never_approved') return null;
  const kind = lesson.items.get(pendingKey(entityType, entityId));
  if (!kind) return null;
  return kind === 'created' ? 'pending-created' : 'pending-updated';
}

/** The Lesson banner's chip: "Not live" for a never-approved Lesson, else its
 *  own pending state. */
export function lessonChip(state: PendingState, lessonId: string): ChipKind | null {
  if (state.status !== 'ready') return null;
  const lesson = state.lessons.get(lessonId);
  if (!lesson) return null;
  if (lesson.state === 'never_approved') return 'not-live';
  return itemChip(state, lessonId, 'learn_lesson', lessonId);
}
